import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DESELECT_CHANNELS_MODAL } from 'modules/workflows';
import { NodeEditor, NodeEditorHeader } from 'modules/workflows/components/NodeEditor';
import { getIsNodeEditorReadOnly, openModal, updateNodeData } from 'modules/workflows/ducks';
import { ChannelOptNodeData } from 'modules/workflows/types';
import { DraggableBoxes } from 'ui/draggableBoxes';
import { InfoFill } from 'ui/icons';
import { colors } from 'ui/theme';

import { editorId } from './ChannelOpt.constants';
import {
  ChannelSelectText,
  ChannelSubText,
  EditorSummaryBackground,
  EditorSummaryIcon,
  EditorSummarySubtext,
  EditorSummaryText,
  NoChannelsEnableContainer,
  NoChannelsEnabledCaption,
  PriorityContainer,
} from './ChannelOptEditor.styled';
import {
  createNextChannels,
  createNextPriorities,
  getChannelOptions,
  getChannelPriorities,
  hasDeselectedChannels,
  parseNodeDataForSaving,
  updateOrder,
} from './ChannelOptEditor.utils';
import { ChannelOptionsSelector } from './ChannelOptionsSelector';
import { ChannelOptEditorProps, ChannelOption } from './types';

export const ChannelOptEditor: FC<React.PropsWithChildren<ChannelOptEditorProps>> = ({
  nodeData = {},
  nodeId,
  ...rest
}) => {
  const dispatch = useDispatch();

  const [channelOptions, setChannelOptions] = useState<Array<ChannelOption>>(
    getChannelOptions(nodeData),
  );
  const [channelPriorities, setChannelPriorities] = useState<Array<ChannelOption>>(() =>
    getChannelPriorities(channelOptions, nodeData),
  );
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    return setChannelPriorities(createNextPriorities(channelOptions));
  }, [channelOptions]);

  const onOptionsUpdate = (selectionId: string) => {
    setIsDirty(true);
    setChannelOptions(createNextChannels(selectionId));
  };
  const onPriorityUpdate = (sourceIndex: number, targetIndex: number) => {
    setIsDirty(true);
    setChannelPriorities(updateOrder(sourceIndex, targetIndex));
  };
  const runUpdate = () => {
    const updatedNodeData = parseNodeDataForSaving({
      nodeData,
      channels: channelPriorities,
    });
    dispatch(updateNodeData(updatedNodeData as ChannelOptNodeData, nodeId));
  };
  const handleUpdate = () => {
    if (hasDeselectedChannels(getChannelOptions(nodeData), channelOptions)) {
      dispatch(
        openModal(DESELECT_CHANNELS_MODAL, {
          onConfirm: runUpdate,
          onCancel: () => null,
        }),
      );
    } else {
      runUpdate();
    }
  };
  const getHasUnsavedChanges = () => isDirty;
  const showSelectChannelsMessage = channelPriorities.length <= 0;
  const isReadOnly = useSelector(getIsNodeEditorReadOnly);
  return (
    <NodeEditor
      {...rest}
      dataTest={editorId}
      onUpdate={handleUpdate}
      getHasUnsavedChanges={getHasUnsavedChanges}
    >
      <EditorSummaryBackground>
        <EditorSummaryIcon />
        <div>
          <EditorSummaryText>Boost engagement with the right channel</EditorSummaryText>
          <EditorSummarySubtext>
            Channel Optimization looks at historical data to determine your users&apos; preferred
            channels
          </EditorSummarySubtext>
        </div>
      </EditorSummaryBackground>
      <ChannelSelectText>Send channels</ChannelSelectText>
      <ChannelSubText>Choose at least two channels</ChannelSubText>
      <ChannelOptionsSelector
        config={channelOptions}
        onSelection={onOptionsUpdate}
        isDisabled={isReadOnly}
      />
      <NodeEditorHeader title="Fallback Options" />
      <ChannelSelectText>Unable to determine channel</ChannelSelectText>
      <ChannelSubText>Choose send preference when there&apos;s inconclusive data</ChannelSubText>
      {showSelectChannelsMessage ? (
        <NoChannelsEnableContainer>
          <InfoFill color={colors.textSecondary} />
          <NoChannelsEnabledCaption>
            Waiting for channels to be selected...
          </NoChannelsEnabledCaption>
        </NoChannelsEnableContainer>
      ) : (
        <PriorityContainer>
          <DraggableBoxes
            draggableBoxes={channelPriorities}
            onUpdate={onPriorityUpdate}
            isDisabled={isReadOnly}
          />
        </PriorityContainer>
      )}
    </NodeEditor>
  );
};
